/** SPEC §19: eigen velden in formulieren en lijsten. */
import { toonVeld, type EigenVeld, type VeldEntiteit } from '@ep/core';
import { useData } from '../lib/data';
import { Veld } from './ui';

export function useVeldDefs(entiteit: VeldEntiteit): EigenVeld[] {
  const { data: d } = useData();
  return (d?.eigenVelden ?? []).filter((v) => v.entiteit === entiteit).sort((a, b) => a.volgorde - b.volgorde);
}

export const useKolomVelden = (entiteit: VeldEntiteit) => useVeldDefs(entiteit).filter((v) => v.kolom);

export const VeldCel = ({ def, waarden }: { def: EigenVeld; waarden?: Record<string, unknown> }) => {
  const v = waarden?.[def.id];
  const t = toonVeld(def, v);
  if (def.soort === 'LINK' && t) return <a href={t} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}>{t.replace(/^https?:\/\//, '').slice(0, 30)}</a>;
  return <>{t || <span className="mute">–</span>}</>;
};

/** Invoer voor alle eigen velden van een soort; waarden op veld-id. */
export function EigenVeldenInvoer({ entiteit, waarden, onChange, kolommen = 2 }: { entiteit: VeldEntiteit; waarden: Record<string, unknown>; onChange: (w: Record<string, unknown>) => void; kolommen?: 1 | 2 }) {
  const defs = useVeldDefs(entiteit);
  if (!defs.length) return null;
  const zet = (id: string, v: unknown) => onChange({ ...waarden, [id]: v === '' ? null : v });
  return (
    <div className="eigen-velden">
      <div className="klein vet mute" style={{ margin: '8px 0 4px' }}>Eigen velden</div>
      <div className={kolommen === 2 ? 'grid2' : 'kolom'}>
        {defs.map((d) => {
          const v = waarden[d.id];
          const label = `${d.naam}${d.verplicht ? ' *' : ''}`;
          const cls = d.soort === 'LANGE_TEKST' && kolommen === 2 ? 'span2' : undefined;
          switch (d.soort) {
            case 'LANGE_TEKST':
              return <Veld key={d.id} label={label} className={cls}><textarea rows={3} value={(v as string) ?? ''} onChange={(e) => zet(d.id, e.target.value)} /></Veld>;
            case 'GETAL':
              return <Veld key={d.id} label={label}><input inputMode="decimal" value={v == null ? '' : String(v)} onChange={(e) => zet(d.id, e.target.value)} /></Veld>;
            case 'DATUM':
              return <Veld key={d.id} label={label}><input type="date" value={(v as string) ?? ''} onChange={(e) => zet(d.id, e.target.value)} /></Veld>;
            case 'KEUZE':
              return (
                <Veld key={d.id} label={label}>
                  <select value={(v as string) ?? ''} onChange={(e) => zet(d.id, e.target.value)}>
                    <option value="">– kies –</option>
                    {d.keuzes.map((k) => <option key={k} value={k}>{k}</option>)}
                  </select>
                </Veld>
              );
            case 'JA_NEE':
              return (
                <Veld key={d.id} label={label}>
                  <select value={v == null ? '' : v ? 'ja' : 'nee'} onChange={(e) => zet(d.id, e.target.value === '' ? null : e.target.value === 'ja')}>
                    <option value="">–</option>
                    <option value="ja">Ja</option>
                    <option value="nee">Nee</option>
                  </select>
                </Veld>
              );
            case 'LINK':
              return <Veld key={d.id} label={label}><input type="url" placeholder="https://" value={(v as string) ?? ''} onChange={(e) => zet(d.id, e.target.value)} /></Veld>;
            default:
              return <Veld key={d.id} label={label}><input value={(v as string) ?? ''} onChange={(e) => zet(d.id, e.target.value)} /></Veld>;
          }
        })}
      </div>
    </div>
  );
}

/** Getallen uit tekstvelden omzetten voor de API (leeg → null). */
export function veldenVoorApi(defs: EigenVeld[], waarden: Record<string, unknown>): Record<string, unknown> {
  const uit: Record<string, unknown> = {};
  for (const d of defs) {
    const v = waarden[d.id];
    uit[d.id] = d.soort === 'GETAL' && typeof v === 'string' ? (v.trim() === '' ? null : Number(v.replace(',', '.'))) : v ?? null;
  }
  return uit;
}

/** Ingevulde eigen velden als lijstje (projectpagina). */
export function EigenVeldenOverzicht({ entiteit, waarden }: { entiteit: VeldEntiteit; waarden?: Record<string, unknown> }) {
  const defs = useVeldDefs(entiteit);
  if (!defs.length) return null;
  return (
    <div className="paneel">
      <h2 style={{ marginTop: 0 }}>Eigen velden</h2>
      <div className="grid3">
        {defs.map((d) => (
          <div key={d.id}>
            <div className="klein mute">{d.naam}</div>
            <div><VeldCel def={d} waarden={waarden} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}
